import * as range from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as node from '../../node'
import type { InstanceHelper } from '../helper'
import type { Instance } from '../instance'
import type { Key } from '../key'
import type { Type } from '../type'

import { DmsDate } from './date'
import { DmsRange, Range } from './range'

/**
 * Generates an instance of the {@link range.Multirange} type
 *
 * @example
 * // Produces an instance based on the provided list of ranges
 * {
 *  "$multirange": [
 *      {
 *        "start": { "$date": "1/1/2020" },
 *        "end": { "$date": "today" }
 *      }
 *      {
 *        "includesStart": true,
 *        "includesEnd": true,
 *        "start": { "$date": "1/1/2020" },
 *        "end": { "$date": { day: 1, month: 1, year: 2025 } }
 *      }
 *    ]
 * }
 * // Produces an empty Multirange instance
 * {
 *  "$multirange": [{}]
 * }
 */
export type Multirange<out T = any> = Readonly<
  Record<
    Key<Type.Multirange>,
    T extends range.Multirange<infer U> ? Multirange.Params<U> : never
  >
>
export namespace Multirange {
  export const isMultirange: TypeChecker<Multirange> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<Multirange>({
        $multirange: TypeChecker.Factory.intersection(
          Params.isParams,
          TypeChecker.isNotNullish,
        ),
      }),
  ).usingAlias('Instance.Multirange')
  export const assertIsMultirange: TypeAsserter<Multirange> =
    TypeAsserter.Factory.from(isMultirange)

  export const HELPER: InstanceHelper<Multirange> = {
    createNode: ({ $multirange: arg }, context) => {
      // TODO: Inline comments
      const ranges: DmsRange<any>[] = arg.map(
        ({ start, end, includesEnd, includesStart }) => {
          const params: range.Range.constructor.Params<any> = {
            includesEnd,
            includesStart,
          }
          if (start) {
            const _node = node.from(start, context)
            node.ForValue.assertIsForValue(_node)
            params.start = _node.value
          }
          if (end) {
            const _node = node.from(end, context)
            node.ForValue.assertIsForValue(_node)
            params.end = _node.value
          }
          return new DmsRange(params)
        },
      )
      return new node.ForValue(new DmsMultiRange(ranges))
    },
    fromPgString: (pgStr: string): DmsMultiRange<any> | undefined => {
      const parsed = range.Multirange.fromPostgres(
        pgStr,
        str => new DmsDate(str),
      )
      return new DmsMultiRange(parsed.ranges)
    },
    fromPlain: ({ $multirange: arg }): DmsMultiRange<any> | undefined => {
      if (!Array.isArray(arg)) {
        return undefined
      }
      const ranges = arg
        .filter(TypeChecker.isNotNullish.raw)
        .filter(TypeChecker.isObject.raw)
        .map(rangeObj =>
          Range.HELPER.fromPlain(rangeObj satisfies unknown as Range),
        )
        .filter(DmsRange.isRange.raw)

      return new DmsMultiRange(ranges)
    },
    runtimeTypeChecker: TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.instance(DmsMultiRange),
    ),
    typeChecker: isMultirange,
  }

  export type Params<T = any> = readonly Range.Params<T>[]
  export namespace Params {
    export const isParams: TypeChecker<Params> = TypeChecker.Factory.list(
      Range.Params.isParams,
    )
  }
}
/** DMS-specific multirange that wraps {@link range.Multirange} with {@link DmsRange} entries */
export class DmsMultiRange<
  in out T extends NonNullable<unknown>,
> extends range.Multirange<T> {
  /** @see {@link range.Multirange.ranges} */
  public override get ranges(): readonly DmsRange<T>[] {
    return super.ranges.map(_range => new DmsRange(_range))
  }

  /** @see {@link range.Multirange.intersection} */
  public override intersection(
    _range: range.Multirange<T> | range.Range<T>,
  ): DmsMultiRange<T> {
    const ranges = super.intersection(_range).ranges
    return new DmsMultiRange(ranges)
  }

  /** @see {@link range.Multirange.union} */
  public override union(
    _range: range.Multirange<T> | range.Range<T>,
  ): DmsMultiRange<T> {
    const ranges = super.union(_range).ranges
    return new DmsMultiRange(ranges)
  }

  /** @see {@link range.Multirange.toJSON} */
  public override toJSON(): range.Multirange.JSON<T> {
    return super.toJSON()
  }
}
